import Phaser from 'phaser';
import {
  BTN_DOWN,
  BTN_FIRE,
  BTN_JUMP,
  BTN_LEAN_LEFT,
  BTN_LEAN_RIGHT,
  BTN_LEFT,
  BTN_RELOAD,
  BTN_RIGHT,
  BTN_WALK,
  BTN_UP,
  BULLET_RADIUS,
  CLOSE_PROTOCOL,
  CLOSE_RESUME,
  CLOSE_UPDATED,
  EV_JOIN,
  EV_KILL,
  EV_LEAVE,
  EV_UPDATE_SOON,
  FLAG_SCREEN_RELATIVE,
  INTERP_DELAY_TICKS,
  Interpolator,
  LAYER_GROUND,
  LAYER_WALLS,
  MAX_PICKUPS,
  MAX_PLAYERS,
  MATCH_INTERMISSION,
  MATCH_PLAYING,
  MODE_TEAM,
  NET_TIMING,
  NO_TEAM,
  MAX_PROJECTILES,
  NO_WINNER,
  PICKUP_CAMO,
  PICKUP_HEALTH,
  PLAYER_RADIUS,
  PROP_AMMO,
  PROP_BRICK,
  PROP_CRATE,
  PROP_HEALTH,
  PROP_NONE,
  TICK_DT,
  TICK_RATE,
  VIEW_FADE_PX,
  VIEW_RADIUS,
  applyInput,
  createWorld,
  parseTiledMap,
  spawnProjectile,
  stepProjectiles,
  type EventMsg,
  type HitMsg,
  type InterpOut,
  type MatchMsg,
  type LevelData,
  type ShotMsg,
  type SnapshotEntry,
  type TiledMap,
  type World,
  MATCH_WARMUP,
  advanceProjectile,
  BTN_SWITCH,
  WEAPON_RIFLE,
  WEAPON_GRENADE,
  GRENADE_RANGE_MIN,
  GRENADE_RANGE_MAX,
  GRENADE_FLIGHT_TICKS,
  GRENADE_RADIUS,
  PROJ_GRENADE,
  spawnGrenade,
  LAYER_WATER,
  LAYER_CANOPY,
  PICKUP_GRENADE,
  propKind,
  type LevelProp,
} from '@mayday/shared';
import { CAMO_SHEET_KEY, LEVEL_KEY, loadLevelImages, type LevelInfo } from './BootScene';
import { HUD_LOG, START_MATCH, UI_NOTICE, UI_ZOOM } from './HudScene';
import { LOBBY_OPEN, PLAY_REQUEST, type PlayTarget } from './lobby';
import { SOLDIER_KEYS, SOLDIER_WALK_FRAMES, makeSoldierSheets } from './soldier-art';
import { TEXT_RESOLUTION, platform, type TouchState } from '@platoon/client-kit';
import { Net } from './net';
import { ACTIONS, SETTINGS_CHANGED, ZOOM_STEP, setCamoProp, setZoom, settings, type Action } from './settings';

// Touch move stick: deflection below this walks, above it runs (running is
// the default on a keyboard too).
const TOUCH_WALK_BELOW = 0.6;

// Team colours by team id (same palette as the scoreboard).
const TEAM_LABELS: Record<number, string> = { 1: 'Blue', 2: 'Red', 3: 'Green', 4: 'Gold' };

interface PendingInput {
  seq: number;
  buttons: number;
  aim: number;
  throwDist: number;
}

// Texture per camouflage prop (brick and crate are frames of the classic
// tileset, loaded on every level for this).
const PROP_TEXTURE: Record<number, string> = {
  [PROP_BRICK]: CAMO_SHEET_KEY,
  [PROP_CRATE]: CAMO_SHEET_KEY,
  [PROP_HEALTH]: 'kit-health',
  [PROP_AMMO]: 'kit-ammo',
};
const MUZZLE_MS = 50;
const HIT_MARKER_MS = 120;

export class GameScene extends Phaser.Scene {
  private net!: Net;
  private myId = -1;
  private level!: LevelData;

  // Local copy of the world. Our own slot is predicted; other players' slots
  // hold their interpolated (render-time) positions; bullets are simulated
  // locally from spawn messages (and predictively for our own) purely for
  // display.
  private world!: World;
  // Remote players are drawn INTERP_DELAY_TICKS in the past, between the two
  // snapshots that bracket the render time. See docs/netcode.md.
  private readonly interp = new Interpolator(MAX_PLAYERS, NET_TIMING);
  private readonly interpOut: InterpOut = { x: 0, y: 0, aim: 0, extrapolated: false };
  private interpExtrapolating = 0; // remote players extrapolated this frame (debug)
  private readonly seen = new Uint8Array(MAX_PLAYERS); // alive per last snapshot
  private readonly sprites: Array<Phaser.GameObjects.Image | null> = new Array<Phaser.GameObjects.Image | null>(MAX_PLAYERS).fill(null);
  // Faint name tag above every other player (hidden while dead or disguised,
  // so camouflage keeps working).
  private readonly labels: Array<Phaser.GameObjects.Text | null> = new Array<Phaser.GameObjects.Text | null>(MAX_PLAYERS).fill(null);
  // Joins whose feed line waits for the newcomer's name (it arrives with the
  // roster a moment after EV_JOIN); flushed when the name shows up or after
  // JOIN_NAME_WAIT_MS with whatever we have.
  private readonly pendingJoins = new Map<number, number>(); // id -> scene time of the join
  private readonly pickupSeen = new Uint8Array(MAX_PICKUPS);
  private readonly pickupSprites: Array<Phaser.GameObjects.Image | null> = new Array<Phaser.GameObjects.Image | null>(MAX_PICKUPS).fill(null);

  private seq = 0;
  private pending: PendingInput[] = [];
  private accumulator = 0;

  // One Phaser Key per bindable action, rebuilt when bindings change.
  private keys!: Record<Action, Phaser.Input.Keyboard.Key>;
  private status = 'connecting…';
  private snapshotTick = 0; // server tick of the snapshot being decoded
  // Match phase from MSG_MATCH; during the intermission nothing is simulated
  // or sent, the HUD shows the final board.
  private matchState = MATCH_PLAYING;
  // Room we are in (from the URL on first connect, then from WELCOME).
  private roomId: string | null = null;
  private awaitingFirstMatch = false; // until the first MSG_MATCH after a WELCOME
  private myPing = 0; // server-measured round trip from the roster, ms
  // Set while leaving one room for another from the lobby: onClose then
  // connects to the new target instead of resuming the old slot.
  private switching: { room: string | null; mode: string | null } | null = null;
  private matchMode = 0; // MODE_DEATHMATCH | MODE_TEAM, from MSG_MATCH
  // A mouse press is latched until the next local tick so a tap shorter
  // than one tick (trackpad tap-to-click, fast clickers) still fires.
  private fireLatch = false;
  // Live server handover (docs/deploy.md): the token the retiring server
  // gave us, how many reconnect attempts we made, and the snapshot tick we
  // are waiting to see advance before declaring the game resumed.
  private resumeToken: string | null = null; // session token (join) or handover token
  private resuming = false;
  private resumeTries = 0;
  private resumeWaitTick = -1;
  private reconnectReason: 'update' | 'lost' = 'lost';
  private resumeTimer: ReturnType<typeof setTimeout> | null = null; // one pending retry at a time
  // Dead-link detection: a "connected" socket that delivers no snapshot for
  // DEAD_LINK_MS is dropped and reconnected (sleep, lock screen, Wi-Fi).
  private lastSnapshotAt = 0;
  private static readonly DEAD_LINK_MS = 3000;
  // zoom keys held (+ and - together = reset)
  private zoomInHeld = false;
  private zoomOutHeld = false;
  // Scratch for the per-frame visibility fade of other players and pickups.
  private static readonly FADE_START = VIEW_RADIUS - VIEW_FADE_PX;
  // Names from the server's roster; "Player N" until it arrives.
  private readonly names = new Map<number, string>();

  // Level currently loaded (hash of its file) and its tile layers, so a
  // changed level can be swapped in place when MSG_MATCH says the room runs
  // another version. See docs/deploy.md "Level changes".
  private levelHash = 0;
  private levelKey: string = LEVEL_KEY; // Phaser cache key of the loaded level
  private mapLayers: Phaser.Tilemaps.TilemapLayer[] = [];
  // Props (palms, huts, crates): sprites per level, rebuilt with the layers.
  private propSprites: Phaser.GameObjects.Image[] = [];
  // Palm crowns and bushes rock slowly: angle = amp * sin(t * speed + phase).
  private swaying: Array<{ target: Phaser.GameObjects.Image; amp: number; speed: number; phase: number }> = [];
  private swappingLevel = false;

  // combat feedback
  private fx!: Phaser.GameObjects.Graphics;
  private lastMuzzleAt = -1e9;
  private lastHitMarkerAt = -1e9;
  private lastKnownHealth = -1;

  constructor() {
    super('game');
  }

  create(info: LevelInfo): void {
    // The same Tiled JSON drives both the renderer and the collision data,
    // so what you see is exactly what the shared sim collides against.
    const raw = (this.cache.tilemap.get(LEVEL_KEY) as { data: TiledMap }).data;
    this.level = parseTiledMap(raw, info.name);
    this.levelHash = info.hash;
    this.world = createWorld(this.level, MAX_PLAYERS);
    this.world.out.authoritative = false; // bullets are visual here; the server decides damage

    this.buildMapLayers(LEVEL_KEY);

    this.makeTextures();
    this.fx = this.add.graphics().setDepth(5);

    this.cameras.main.setBounds(0, 0, this.level.widthPx, this.level.heightPx);
    this.cameras.main.setBackgroundColor(this.level.backgroundColor ?? '#0b0f0c');

    const kb = this.input.keyboard!;
    this.bindKeys();
    this.input.on(Phaser.Input.Events.POINTER_DOWN, (p: Phaser.Input.Pointer) => {
      if (p.leftButtonDown()) this.fireLatch = true;
    });
    kb.addCapture(['UP', 'DOWN', 'LEFT', 'RIGHT']); // arrows never scroll the page
    this.input.mouse?.disableContextMenu();

    // HUD runs as its own scene on top and reads these.
    this.registry.set('level', this.level);
    this.registry.set('world', this.world);
    this.registry.set('myId', -1);
    this.registry.set('health', 100);
    this.registry.set('weapon', 'RIFLE');
    this.registry.set('ammo', 0);
    this.registry.set('ammoReserve', 0);
    this.registry.set('reloading', false);
    this.registry.set('dead', false);
    this.registry.set('protected', false);
    this.registry.set('score', 0);
    this.registry.set('mode', 'Deathmatch'); // TODO(rooms): from room settings
    this.registry.set('match', null);
    this.scene.launch('hud');

    this.net = new Net({
      onWelcome: (id, _tickRate, roomId) => this.onWelcome(id, roomId),
      onEvent: (e) => this.onEvent(e),
      onShot: (s) => this.onShot(s),
      onHit: (h) => this.onHit(h),
      onMatch: (m) => this.onMatch(m),
      onResumeToken: (hex) => {
        this.resumeToken = hex;
      },
      onScores: (entries) => {
        this.registry.set('scores', entries);
        // Teams come with the roster; the shared sim uses them for friendly
        // fire, the renderer for colours.
        for (const e of entries) this.world.team[e.id] = e.team;
        const mine = entries.find((e) => e.id === this.myId);
        this.registry.set('myTeam', mine ? mine.team : NO_TEAM);
        for (const e of entries) {
          this.names.set(e.id, e.name);
          // The roster carries real names; a join waiting for one can log now.
          if (this.pendingJoins.has(e.id) && e.name && !/^Player \d+$/.test(e.name)) this.flushJoin(e.id);
        }
        const me = entries.find((e) => e.id === this.myId);
        if (me) {
          this.registry.set('score', me.kills);
          this.myPing = me.ping;
        }
      },
      onSnapshotBegin: (tick) => {
        this.snapshotTick = tick;
        this.lastSnapshotAt = this.time.now;
        if (this.resuming) this.onResumeSnapshot(tick);
        this.interp.onSnapshot(tick, this.time.now);
        this.seen.fill(0);
        this.pickupSeen.fill(0);
      },
      onSnapshotEntry: (e) => this.onSnapshotEntry(e),
      onSnapshotPickup: (p) => {
        const w = this.world;
        this.pickupSeen[p.slot] = 1;
        w.kAlive[p.slot] = 1;
        w.kKind[p.slot] = p.kind;
        w.kX[p.slot] = p.x;
        w.kY[p.slot] = p.y;
        w.kTtl[p.slot] = 65535; // the server owns expiry; keep ours alive
      },
      onSnapshotEnd: () => this.onSnapshotEnd(),
      onClose: (code) => this.onClose(code),
    });
    // A page reload in the middle of a handover (protocol bump) left the
    // token in the platform store; use it once if it is fresh.
    this.roomId = platform.getRoomFromUrl();
    const mode = platform.getQueryParam('mode');
    const saved = this.takeSavedResumeToken();
    if (this.roomId || mode || saved) {
      this.net.connect({ resume: saved, room: this.roomId, mode });
    } else {
      // Plain visit: the lobby overlay decides where to play (docs/screens/lobby.md).
      this.status = 'idle';
      this.game.events.emit(LOBBY_OPEN);
    }
    this.lastSnapshotAt = this.time.now;
    const onPlay = (target: PlayTarget) => this.joinRoom(target);
    this.game.events.on(PLAY_REQUEST, onPlay);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.game.events.off(PLAY_REQUEST, onPlay));
    // Back from sleep or offline: don't wait for the 3 s check.
    platform.onWake(() => {
      if (this.status === 'connected' && this.time.now - this.lastSnapshotAt > GameScene.DEAD_LINK_MS) this.net.close();
    });

    this.setupZoom();

    // Nickname / camouflage choice changes go straight to the server; key
    // bindings are re-read.
    const onSettings = () => {
      if (settings.nickname) this.net.sendName(settings.nickname);
      if (settings.camoProp) this.net.sendProp(settings.camoProp);
      this.bindKeys();
    };
    this.game.events.on(SETTINGS_CHANGED, onSettings);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.game.events.off(SETTINGS_CHANGED, onSettings));
  }

  override update(_time: number, deltaMs: number): void {
    // Fixed-step prediction loop, same TICK_DT as the server.
    this.accumulator += Math.min(deltaMs / 1000, 0.25);
    while (this.accumulator >= TICK_DT) {
      this.tickLocal();
      stepProjectiles(this.world, TICK_DT);
      this.noteContacts();
      this.accumulator -= TICK_DT;
    }
    // A socket can look open long after the link died (sleep, lock screen).
    if (this.status === 'connected' && this.time.now - this.lastSnapshotAt > GameScene.DEAD_LINK_MS) {
      this.status = 'stalled';
      this.net.close(); // onClose takes it from here
    }
    const t = this.time.now;
    for (const s of this.swaying) s.target.setAngle(s.amp * Math.sin(t * s.speed + s.phase));
    this.render();
  }

  // ---- hit feedback ---------------------------------------------------------
  // Our copy of the simulation records where bullets touched bodies this
  // tick (never damage: that is the server's). The body flashes for everyone
  // who saw the contact; if the body is ours we also remember the direction
  // for the hurt arc. docs/health-and-damage.md "Hit feedback".
  private static readonly FLASH_MS = 150;
  private static readonly HURT_MS = 350;
  private static readonly HURT_MATCH_MS = 400; // a local contact this recent explains a health drop
  private readonly flashUntil = new Float64Array(MAX_PLAYERS);
  private hurtAt = -1e9;
  private hurtAngle = NaN; // where the hit came from (world angle), NaN = unknown
  private hurtStrength = 0;
  private lastKillMarkerAt = -1e9;

  private static readonly BOOM_MS = 320;
  private static readonly BOOM_SLOTS = 16;
  private readonly boomX = new Float32Array(GameScene.BOOM_SLOTS);
  private readonly boomY = new Float32Array(GameScene.BOOM_SLOTS);
  private readonly boomAt = new Float64Array(GameScene.BOOM_SLOTS).fill(-1e9);
  private boomNext = 0;
  private throwTarget = -1; // px, when the grenade is out: where it would land

  private noteContacts(): void {
    const w = this.world;
    const now = this.time.now;
    for (let b = 0; b < w.out.boomCount; b++) {
      const k = this.boomNext;
      this.boomNext = (k + 1) % GameScene.BOOM_SLOTS;
      this.boomX[k] = w.boomX[b]!;
      this.boomY[k] = w.boomY[b]!;
      this.boomAt[k] = now;
      if (this.myId >= 0) {
        const d = Math.hypot(w.boomX[b]! - w.x[this.myId]!, w.boomY[b]! - w.y[this.myId]!);
        if (d < 500) this.cameras.main.shake(160, 0.006 * (1 - d / 500));
      }
    }
    for (let n = 0; n < w.out.hitCount; n++) {
      const victim = w.hitVictim[n]!;
      this.flashUntil[victim] = now + GameScene.FLASH_MS;
      if (victim === this.myId) {
        // The bullet is gone; the shooter's position is where it came from.
        // The server's snapshot may have reported the damage already (or
        // will shortly): the two reports describe one hit, so keep the
        // stronger figure and the earlier start.
        const shooter = w.hitShooter[n]!;
        this.hurtAngle = Math.atan2(w.y[shooter]! - w.y[victim]!, w.x[shooter]! - w.x[victim]!);
        const recent = now - this.hurtAt < GameScene.HURT_MATCH_MS;
        if (!recent) this.hurtAt = now;
        this.hurtStrength = Math.max(recent ? this.hurtStrength : 0, Math.min(1, w.hitDamage[n]! / 50));
        this.registry.set('hurt', { at: this.hurtAt, strength: this.hurtStrength });
      }
    }
  }

  // ---- zoom -----------------------------------------------------------------
  // + / - keys (hold both to reset), mouse wheel, touch buttons via UI_ZOOM.
  // Smallest zoom that still shows the whole level (never above 1).
  private minZoom(): number {
    return Math.min(1, this.scale.width / this.level.widthPx, this.scale.height / this.level.heightPx);
  }

  // Camera bounds: the level, widened symmetrically when the (zoomed-out)
  // view is larger than the level, so the whole map sits centred instead
  // of hugging the top-left corner while follow keeps working.
  private setCameraZoom(z: number): void {
    const cam = this.cameras.main;
    cam.setZoom(z);
    const viewW = this.scale.width / z;
    const viewH = this.scale.height / z;
    const w = Math.max(viewW, this.level.widthPx);
    const h = Math.max(viewH, this.level.heightPx);
    cam.setBounds(-(w - this.level.widthPx) / 2, -(h - this.level.heightPx) / 2, w, h);
  }

  private setupZoom(): void {
    this.setCameraZoom(setZoom(settings.zoom, this.minZoom()));
    this.scale.on(Phaser.Scale.Events.RESIZE, () => this.setCameraZoom(setZoom(settings.zoom, this.minZoom())));
    const isIn = (ev: KeyboardEvent) => ev.key === '+' || ev.key === '=' || ev.code === 'NumpadAdd';
    const isOut = (ev: KeyboardEvent) => ev.key === '-' || ev.key === '_' || ev.code === 'NumpadSubtract';
    const onKey = (ev: KeyboardEvent) => {
      if (this.registry.get('uiOpen') === true) return;
      const down = ev.type === 'keydown';
      if (isIn(ev)) this.zoomInHeld = down;
      else if (isOut(ev)) this.zoomOutHeld = down;
      else return;
      if (!down) return;
      ev.preventDefault();
      if (this.zoomInHeld && this.zoomOutHeld) this.applyZoom(0);
      else this.applyZoom(isIn(ev) ? 1 : -1);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    this.input.on(Phaser.Input.Events.POINTER_WHEEL, (_p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => {
      if (this.registry.get('uiOpen') === true || dy === 0) return;
      this.applyZoom(dy < 0 ? 1 : -1);
    });
    this.game.events.on(UI_ZOOM, (delta: number) => this.applyZoom(delta));
    const onStart = () => this.net.sendStart();
    this.game.events.on(START_MATCH, onStart);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.game.events.off(START_MATCH, onStart));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
    });
  }

  private applyZoom(delta: number): void {
    const z = delta === 0 ? 1 : settings.zoom * (delta > 0 ? ZOOM_STEP : 1 / ZOOM_STEP);
    const v = setZoom(Math.abs(z - 1) < 0.02 ? 1 : z, this.minZoom()); // snap to 1 when passing through
    this.setCameraZoom(v);
  }


  private log(message: string, color?: string): void {
    this.game.events.emit(HUD_LOG, message, color);
  }

  private nameOf(id: number): string {
    return id === this.myId ? 'You' : (this.names.get(id) ?? `Player ${id}`);
  }

  // ---- networking -----------------------------------------------------------

  // From the lobby: play in `room`, or quick play for `mode`. Leaves the
  // current room (its slot expires after the reconnect grace) and joins
  // the new one in place; a different level arrives through MSG_MATCH's
  // hash and the live swap, so nothing reloads.
  joinRoom(target: PlayTarget): void {
    const next = { room: target.room ?? null, mode: target.mode ?? null };
    if (this.status === 'connected' && next.room !== null && next.room === this.roomId) return; // already there
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.resumeTimer = null;
    this.resuming = false;
    this.resumeToken = null;
    this.game.events.emit(UI_NOTICE, '');
    if (this.status === 'connected' || this.status === 'stalled') {
      this.switching = next;
      this.net.close(); // onClose finishes the switch
      return;
    }
    this.startJoin(next);
  }

  private startJoin(next: { room: string | null; mode: string | null }): void {
    this.leaveWorld();
    this.roomId = next.room;
    this.status = 'connecting…';
    this.net.connect({ resume: null, room: next.room, mode: next.mode });
  }

  // Forget everything about the room we were in.
  private leaveWorld(): void {
    this.clearOtherPlayers();
    if (this.myId >= 0) {
      this.world.alive[this.myId] = 0;
      this.interp.clear(this.myId);
      this.sprites[this.myId]?.destroy();
      this.sprites[this.myId] = null;
      this.labels[this.myId]?.destroy();
      this.labels[this.myId] = null;
    }
    this.cameras.main.stopFollow();
    this.myId = -1;
    this.pending.length = 0;
    this.world.pAlive.fill(0);
    this.world.kAlive.fill(0);
    this.names.clear();
    this.pendingJoins.clear();
    this.registry.set('myId', -1);
    this.registry.set('scores', []);
    this.registry.set('score', 0);
    this.registry.set('dead', false);
    this.registry.set('match', null);
  }

  private onWelcome(id: number, roomId: string): void {
    this.myId = id;
    this.awaitingFirstMatch = true;
    this.status = 'connected';
    // The server decided the room (the one we asked for, quick play, or a
    // resume); the address bar becomes the invite link.
    this.roomId = roomId;
    this.registry.set('room', roomId);
    platform.setRoomInUrl(roomId);
    this.world.alive[id] = 1;
    this.registry.set('myId', id);
    this.cameras.main.startFollow(this.ensureSprite(id), true, 0.15, 0.15);
    if (this.resuming) {
      // Same slot (token accepted) or a fresh one (token expired): either
      // way forget what the old connection owed us and start interpolation
      // fresh from the first snapshot. Our old slot's sprite, if the id
      // changed, goes with the next snapshot's cleanup.
      this.pending.length = 0;
      this.world.pAlive.fill(0);
      for (let i = 0; i < MAX_PLAYERS; i++) this.interp.clear(i);
      if (settings.nickname) this.net.sendName(settings.nickname);
      if (settings.camoProp) this.net.sendProp(settings.camoProp);
      return;
    }
    if (settings.nickname) this.net.sendName(settings.nickname);
    if (settings.camoProp) this.net.sendProp(settings.camoProp);
    this.log(`Connected as ${settings.nickname || `Player ${id}`}`, '#9fd49a');
  }

  // Deploy handover: the old server sends us off once its match is over
  // (CLOSE_UPDATED) and the new one is already on the port; a page built
  // against another protocol gets CLOSE_PROTOCOL. Both: reload.
  private onClose(code: number): void {
    this.status = 'disconnected';
    if (this.switching !== null) {
      const next = this.switching;
      this.switching = null;
      this.startJoin(next);
      return;
    }
    if (code === CLOSE_UPDATED || code === CLOSE_PROTOCOL) {
      // A protocol bump mid-handover: keep the token across the reload.
      if (code === CLOSE_PROTOCOL && this.resumeToken) {
        platform.saveSetting('resume', `${this.resumeToken}:${Date.now()}`);
      }
      this.game.events.emit(
        UI_NOTICE,
        code === CLOSE_UPDATED
          ? 'Updating Mayday Platoon…\nHang tight, you’ll be back in the fight in a few seconds.'
          : 'A new version is ready.\nReloading…',
        'overlay',
      );
      setTimeout(() => platform.reload(), 1500);
      return;
    }
    // Anything else (handover CLOSE_RESUME, a dead link, a server restart):
    // reconnect. The session token puts us back into our slot with our score
    // if the server still holds it; otherwise we join fresh.
    if (!this.resuming) {
      this.reconnectReason = code === CLOSE_RESUME ? 'update' : 'lost';
      this.clearOtherPlayers();
    }
    this.scheduleResume();
  }

  // Handover: the successor is up within a second; give up and reload after
  // 5 s. Lost link: keep trying forever, slowly, until the network is back.
  private static readonly RESUME_FAST_TRIES = 20;
  private static readonly RESUME_RETRY_MS = 250;
  private static readonly RECONNECT_SLOW_MS = 3000;

  private scheduleResume(): void {
    if (!this.resuming) {
      this.resuming = true;
      this.resumeTries = 0;
      this.resumeWaitTick = -1;
      this.game.events.emit(
        UI_NOTICE,
        this.reconnectReason === 'update'
          ? 'Server update in progress…\nHold on, the match resumes in a moment.'
          : 'Connection lost.\nReconnecting…',
        'overlay',
      );
    }
    this.resumeTries++;
    if (this.reconnectReason === 'update' && this.resumeTries > GameScene.RESUME_FAST_TRIES) {
      this.game.events.emit(UI_NOTICE, 'Could not rejoin the updated server.\nReloading…', 'overlay');
      setTimeout(() => platform.reload(), 1000);
      return;
    }
    const delay = this.resumeTries <= GameScene.RESUME_FAST_TRIES ? GameScene.RESUME_RETRY_MS : GameScene.RECONNECT_SLOW_MS;
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => {
      this.resumeTimer = null;
      this.net.connect({ resume: this.resumeToken, room: this.roomId });
    }, delay);
  }

  // Nothing we show about other players is current once the link is gone;
  // the first snapshots after reconnecting rebuild them.
  private clearOtherPlayers(): void {
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (id === this.myId) continue;
      this.world.alive[id] = 0;
      this.interp.clear(id);
      this.sprites[id]?.destroy();
      this.sprites[id] = null;
      this.labels[id]?.destroy();
      this.labels[id] = null;
    }
    this.world.pAlive.fill(0);
  }

  // First snapshot after a resume: the room may still be frozen, so wait
  // until the server tick moves before taking the overlay down.
  private onResumeSnapshot(tick: number): void {
    if (this.resumeWaitTick < 0) {
      this.resumeWaitTick = tick;
    } else if (tick > this.resumeWaitTick) {
      this.resuming = false;
      this.game.events.emit(UI_NOTICE, '');
      this.log(this.reconnectReason === 'update' ? 'Server updated. Back in the fight.' : 'Reconnected.', '#9fd49a');
    }
  }

  private takeSavedResumeToken(): string | null {
    const saved = platform.loadSetting('resume');
    if (!saved) return null;
    platform.saveSetting('resume', '');
    const [hex, at] = saved.split(':');
    if (!hex || !at || Date.now() - Number(at) > 30_000) return null;
    this.resuming = true;
    this.resumeTries = 1;
    this.resumeWaitTick = -1;
    this.resumeToken = hex;
    return hex;
  }

  private onEvent(e: EventMsg): void {
    switch (e.kind) {
      case EV_UPDATE_SOON:
        this.log('Server update after this match. Stay put, you’ll be back in seconds.', '#e8d7a8');
        this.game.events.emit(
          UI_NOTICE,
          'A server update is on its way.\nStay right here: this match plays out to the end, then a quick restart and you’re back in the fight within seconds.',
          'toast',
        );
        break;
      case EV_JOIN:
        // The newcomer's name arrives with the next roster (the server
        // pushes one as soon as the client sends its nickname); wait for it
        // so the line reads "Yiannis joined" rather than "Player 3 joined".
        if (e.id !== this.myId) {
          this.names.delete(e.id); // a reused slot must not inherit the previous occupant's name
          this.pendingJoins.set(e.id, this.time.now);
        }
        break;
      case EV_LEAVE:
        if (e.id !== this.myId) {
          if (this.pendingJoins.has(e.id)) this.flushJoin(e.id);
          this.log(`${this.nameOf(e.id)} left`, '#c9c9a0');
          this.names.delete(e.id);
        }
        break;
      case EV_KILL: {
        if (e.id2 === this.myId && e.id !== this.myId) this.lastKillMarkerAt = this.time.now;
        const mine = e.id2 === this.myId;
        const me = e.id === this.myId;
        this.log(
          e.id === e.id2 ? `${this.nameOf(e.id)} ${me ? 'blew yourself' : 'blew themselves'} up` : `${this.nameOf(e.id)} ${me ? 'were' : 'was'} hunted down by ${this.nameOf(e.id2)}`,
          mine ? '#8fd98a' : me ? '#e07a7a' : '#e6efe0',
        );
        if (me) {
          // Death screen: who did it, and a countdown to respawn.
          this.registry.set('deathInfo', { killer: this.nameOf(e.id2), at: this.time.now });
        }
        break;
      }
    }
  }

  // (Re)create the Phaser keys from the current bindings. Old keys are
  // removed so a key that is no longer bound stops being tracked.
  private slotKeys!: { one: Phaser.Input.Keyboard.Key; two: Phaser.Input.Keyboard.Key };

  private bindKeys(): void {
    const kb = this.input.keyboard!;
    // Fixed slot keys (addKey returns the existing Key on a rebind).
    this.slotKeys = { one: kb.addKey(Phaser.Input.Keyboard.KeyCodes.ONE, true), two: kb.addKey(Phaser.Input.Keyboard.KeyCodes.TWO, true) };
    // `keys` is unset until the first bindKeys() (declared with `!`).
    if ((this.keys as Record<Action, Phaser.Input.Keyboard.Key> | undefined) !== undefined) {
      for (const key of Object.values(this.keys)) kb.removeKey(key, false, true);
    }
    const b = settings.bindings;
    this.keys = {} as Record<Action, Phaser.Input.Keyboard.Key>;
    const codes: number[] = [];
    for (const { action } of ACTIONS) {
      this.keys[action] = kb.addKey(b[action].code, true);
      codes.push(b[action].code);
    }
    // Keep bound keys (Space, arrows...) from scrolling the page.
    kb.addCapture(codes);
  }

  private static readonly JOIN_NAME_WAIT_MS = 1500;

  private flushJoin(id: number): void {
    this.pendingJoins.delete(id);
    this.log(`${this.nameOf(id)} joined`, '#9fd49a');
  }

  private flushStaleJoins(now: number): void {
    if (this.pendingJoins.size === 0) return;
    for (const [id, at] of this.pendingJoins) {
      if (now - at >= GameScene.JOIN_NAME_WAIT_MS) this.flushJoin(id);
    }
  }

  // Depth plan: tile layers -20 and up (the classic walls at -10, the jungle
  // terrain sets in file order), prop shadows -9, ground props -8, pickups
  // -5, soldiers 0, props you walk under (palm crowns, bushes, hut roofs)
  // and the classic canopy 2, name tags 4, fx 5.
  private buildMapLayers(key: string): void {
    for (const l of this.mapLayers) l.destroy();
    for (const s of this.propSprites) s.destroy();
    this.mapLayers = [];
    this.propSprites = [];
    this.swaying = [];
    const map = this.make.tilemap({ key });
    const raw = (this.cache.tilemap.get(key) as { data: TiledMap }).data;
    // Sheet tilesets are loaded under their image path (BootScene). Image
    // collections (the props) have no sheet and are drawn from level.props.
    const tilesets: Phaser.Tilemaps.Tileset[] = [];
    for (const ts of raw.tilesets ?? []) {
      if (!ts.image) continue;
      const set = map.addTilesetImage(ts.name, ts.image);
      if (set) tilesets.push(set);
    }
    const classicDepth: Record<string, number> = { [LAYER_GROUND]: -20, [LAYER_WATER]: -15, [LAYER_WALLS]: -10, [LAYER_CANOPY]: 2 };
    let i = 0;
    for (const l of raw.layers) {
      if (l.type !== 'tilelayer') continue;
      const layer = map.createLayer(l.name, tilesets);
      if (layer) this.mapLayers.push(layer.setDepth(classicDepth[l.name] ?? -20 + i * 0.1));
      i++;
    }
    for (const p of this.level.props) this.addProp(p);
  }

  // A prop's sprites. The palm is three: a soft shadow on the ground, the
  // trunk you collide with, and the crown drawn above the soldiers, so you
  // walk under the fronds and pass behind the tree; crown and shadow rock
  // slowly. Bushes rock too; everything else is a still image.
  private addProp(p: LevelProp): void {
    const kind = propKind(p.kind);
    if (!kind || !p.image || !this.textures.exists(p.image)) return;
    const over = kind.layer === 'over';
    const img = this.add.image(p.x, p.y, p.image).setDepth(over ? 2 : -8);
    this.propSprites.push(img);
    const phase = ((p.x * 7919 + p.y * 104729) % 628) / 100; // deterministic, per tree
    if (kind.name === 'palm') {
      const trunkKey = p.image.replace(/[^/]+$/, 'palm-trunk.png');
      const shadow = this.add.image(p.x + 10, p.y + 14, p.image).setTint(0x000000).setAlpha(0.28).setDepth(-9);
      this.propSprites.push(shadow);
      if (this.textures.exists(trunkKey)) this.propSprites.push(this.add.image(p.x, p.y, trunkKey).setDepth(-8));
      img.setY(p.y - 4).setAlpha(0.88); // a soldier under the fronds stays faintly visible
      this.swaying.push({ target: img, amp: 2.5, speed: 0.0009, phase }, { target: shadow, amp: 2.5, speed: 0.0009, phase });
    } else if (kind.name === 'bush') {
      this.swaying.push({ target: img, amp: 1.5, speed: 0.0014, phase });
    }
  }

  // The room runs another version of the level than the one we loaded
  // (a deploy changed the file; we were resumed or kept playing). Fetch
  // it and swap tiles, collision data and the map preview in place; the
  // match goes on. The tileset image is not reloaded (a tileset change
  // still needs a page reload).
  private swapLevel(hash: number): void {
    if (this.swappingLevel || hash === this.levelHash) return;
    this.swappingLevel = true;
    fetch('/api/level' + (this.roomId ? `?room=${encodeURIComponent(this.roomId)}` : ''))
      .then((r) => r.json() as Promise<LevelInfo>)
      .then((info) => {
        const key = `${LEVEL_KEY}:${info.hash}`;
        const previousKey = this.levelKey;
        this.load.tilemapTiledJSON(key, info.url);
        loadLevelImages(this, info); // a room switch may bring another theme's sheets
        this.load.once(Phaser.Loader.Events.COMPLETE, () => {
          const raw = (this.cache.tilemap.get(key) as { data: TiledMap }).data;
          const next = parseTiledMap(raw, info.name);
          this.level = next;
          (this.world as { level: LevelData }).level = next; // what prediction collides against
          this.levelHash = info.hash;
          this.buildMapLayers(key);
          this.cameras.main.setBackgroundColor(next.backgroundColor ?? '#0b0f0c');
          this.levelKey = key;
          this.cache.tilemap.remove(previousKey); // the old level's JSON is not needed any more
          this.setCameraZoom(setZoom(settings.zoom, this.minZoom()));
          this.registry.set('level', next); // the HUD rebuilds its map preview
          this.swappingLevel = false;
          this.log('Level updated', '#e8d7a8');
        });
        this.load.start();
      })
      .catch(() => {
        this.swappingLevel = false; // the next MSG_MATCH (1 Hz) tries again
      });
  }

  // A remote bullet reaches us one way-trip after it was fired, so it is
  // spawned where the server has it NOW, not at the muzzle: the server tick
  // it carries against our estimate of the server's current tick (last
  // snapshot tick + time since + half our ping). Our own bullets were
  // spawned predictively when we fired and take no part in this.
  private onShot(s: ShotMsg): void {
    if (s.owner === this.myId) return;
    const slot = s.kind === PROJ_GRENADE ? spawnGrenade(this.world, s.owner, s.x, s.y, s.angle, s.dist) : spawnProjectile(this.world, s.owner, s.x, s.y, s.angle);
    if (slot < 0) return;
    const sinceSnapshot = (this.time.now - this.lastSnapshotAt) / 1000 / TICK_DT;
    const serverTickNow = this.snapshotTick + sinceSnapshot + (this.myPing / 2) / 1000 / TICK_DT;
    const age = Math.min(GameScene.SHOT_CATCHUP_MAX_TICKS, Math.max(0, Math.round(serverTickNow - s.tick)));
    if (age > 0) advanceProjectile(this.world, slot, age, TICK_DT);
    if (import.meta.env.DEV) this.registry.set('shotAge', age);
  }
  private static readonly SHOT_CATCHUP_MAX_TICKS = 60; // a second; beyond that the clock estimate is wrong, not the bullet

  private onHit(_h: HitMsg): void {
    this.lastHitMarkerAt = this.time.now;
  }

  // Match state and clock. Arrives on join, on every phase change and once
  // a second; the HUD counts down from `secondsLeft` between messages.
  private onMatch(m: MatchMsg): void {
    if (m.levelHash !== 0 && m.levelHash !== this.levelHash) this.swapLevel(m.levelHash);
    const changed = m.state !== this.matchState;
    this.matchState = m.state;
    this.matchMode = m.mode;
    this.world.friendlyFire = m.mode !== MODE_TEAM || m.friendlyFire;
    this.registry.set('mode', m.mode === MODE_TEAM ? 'Team match' : 'Deathmatch');
    const winnerName = m.winner === NO_WINNER ? '' : this.nameOf(m.winner);
    this.registry.set('match', {
      state: m.state,
      winner: m.winner,
      winnerName,
      mode: m.mode,
      winnerTeam: m.winnerTeam,
      killLimit: m.killLimit,
      timeLimitS: m.timeLimitS,
      secondsLeft: m.secondsLeft,
      teams: m.teams,
      teamSize: m.teamSize,
      at: this.time.now,
    });
    // First word from a room we just joined: say so when the match is
    // already under way (quick play backfilled us).
    if (this.awaitingFirstMatch) {
      this.awaitingFirstMatch = false;
      if (m.state === MATCH_PLAYING && m.secondsLeft < m.timeLimitS - 5) {
        const mm = Math.floor(m.secondsLeft / 60);
        const ss = m.secondsLeft % 60;
        this.game.events.emit(UI_NOTICE, `Joined a match in progress · ${mm}:${ss < 10 ? '0' : ''}${ss} left`, 'toast', 5000);
      }
    }
    if (!changed) return;
    if (m.state === MATCH_WARMUP) {
      this.log('Warm-up: shoot around, nothing counts. The match starts when the room fills, when someone presses Start, or when the clock runs out.', '#e8d7a8');
      return;
    }
    if (m.state === MATCH_INTERMISSION) {
      // Bullets die with the match (the server dropped its own); inputs we
      // sent in the last moments will never be acknowledged, so forget them.
      this.world.pAlive.fill(0);
      this.pending.length = 0;
      if (m.mode === MODE_TEAM) {
        const mine = (this.registry.get('myTeam') as number | undefined) ?? NO_TEAM;
        this.log(m.winnerTeam === NO_TEAM ? 'Match over: draw' : `Match over: ${TEAM_LABELS[m.winnerTeam] ?? `Team ${m.winnerTeam}`} team ${m.winnerTeam === mine ? 'wins (that is you)' : 'wins'}`, '#e8d7a8');
      } else {
        this.log(winnerName ? `Match over: ${winnerName} ${m.winner === this.myId ? 'win' : 'wins'}` : 'Match over: draw', '#e8d7a8');
      }
    } else {
      // From the intermission or the warm-up: everyone was respawned, our
      // in-flight inputs and bullets mean nothing now.
      this.world.pAlive.fill(0);
      this.pending.length = 0;
      this.log(`New match: first to ${m.killLimit} kills`, '#e8d7a8');
    }
  }

  private onSnapshotEntry(e: SnapshotEntry): void {
    const w = this.world;
    this.seen[e.id] = 1;
    w.alive[e.id] = 1;
    w.health[e.id] = e.health;
    w.invuln[e.id] = e.invuln;
    // What the server says this player looks like right now. For ourselves
    // the local prediction below recomputes it each tick anyway.
    w.disguised[e.id] = e.disguise;

    if (e.id === this.myId) {
      // Reconcile: snap to the authoritative state (position and weapon
      // timers), then replay the inputs the server hasn't processed yet.
      // Replay must not re-spawn the bullets those inputs already fired.
      const predictedX = w.x[e.id]!; // where prediction had us; the correction is the gap after replay
      const predictedY = w.y[e.id]!;
      w.x[e.id] = e.x;
      w.y[e.id] = e.y;
      w.ammo[e.id] = e.ammo;
      w.reserve[e.id] = e.reserve;
      w.cooldown[e.id] = e.cooldown;
      w.reload[e.id] = e.reload;
      w.camoTicks[e.id] = e.camoTicks;
      w.camoProp[e.id] = e.camoProp;
      w.weapon[e.id] = e.weapon;
      w.switchTicks[e.id] = e.switchTicks;
      w.grenades[e.id] = e.grenades;
      w.throwCooldown[e.id] = e.throwCooldown;
      // First camouflage with no choice made: the server picked one for us;
      // adopt it as our saved choice so it sticks next time.
      if (e.camoProp !== 0 && settings.camoProp === 0) setCamoProp(e.camoProp);
      w.out.suppressSpawns = true;
      let keep = 0;
      for (const p of this.pending) {
        if (p.seq <= e.lastSeq) continue;
        applyInput(w, e.id, p.seq, p.buttons, p.aim, TICK_DT, p.throwDist);
        this.pending[keep++] = p;
      }
      this.pending.length = keep;
      w.out.suppressSpawns = false;
      if (import.meta.env.DEV) {
        // Dev probe: how much a reconcile moved us (0 when prediction was exact).
        const d = Math.hypot(w.x[e.id]! - predictedX, w.y[e.id]! - predictedY);
        const rc = (this.registry.get('reconcile') as { count: number; corrections: number; max: number; sum: number; teleports: number } | undefined) ?? { count: 0, corrections: 0, max: 0, sum: 0, teleports: 0 };
        rc.count++;
        if (d > 60) rc.teleports++; // spawns and respawns, not prediction error
        else if (d > 0.5) {
          rc.corrections++;
          rc.sum += d;
          if (d > rc.max) rc.max = d;
          const log = (this.registry.get('reconcileLog') as unknown[] | undefined) ?? [];
          if (log.length < 60) log.push({ seq: e.lastSeq, mySeq: this.seq, replayed: keep, dx: +(w.x[e.id]! - predictedX).toFixed(2), dy: +(w.y[e.id]! - predictedY).toFixed(2), btn: w.buttons[e.id], t: Math.round(this.time.now) });
          this.registry.set('reconcileLog', log);
        }
        this.registry.set('reconcile', rc);
      }

      // Hurt: the server says our health dropped. The local contact (if
      // our copy of the bullet touched us too) already set the direction.
      if (this.lastKnownHealth >= 0 && e.health < this.lastKnownHealth) {
        const now = this.time.now;
        const fromContact = now - this.hurtAt < GameScene.HURT_MATCH_MS;
        if (!fromContact) {
          this.hurtAt = now;
          this.hurtAngle = NaN; // no direction known
        }
        this.hurtStrength = Math.max(fromContact ? this.hurtStrength : 0, Math.min(1, (this.lastKnownHealth - e.health) / 50));
        this.registry.set('hurt', { at: this.hurtAt, strength: this.hurtStrength });
      }
      this.lastKnownHealth = e.health;
      this.registry.set('health', e.health);
      this.registry.set('ammo', w.ammo[e.id]);
      this.registry.set('ammoReserve', w.reserve[e.id]);
      this.registry.set('grenades', w.grenades[e.id]);
      this.registry.set('reloading', w.reload[e.id]! > 0);
      this.registry.set('dead', e.health === 0);
      this.registry.set('protected', e.invuln > 0);
      this.registry.set('camoTicks', e.camoTicks);
      this.registry.set('camoProp', e.camoProp || settings.camoProp);
    } else {
      // Remote players: into the interpolation buffer; render() resolves the
      // position for the (delayed) render time each frame. What they hold is
      // drawn from the latest snapshot as is.
      w.weapon[e.id] = e.weapon;
      if (!this.interp.has(e.id)) {
        // First sight: show them where they are right now rather than at
        // (0,0) until the buffer fills.
        w.x[e.id] = e.x;
        w.y[e.id] = e.y;
        w.aim[e.id] = e.aim;
      }
      this.interp.push(e.id, this.snapshotTick, e.x, e.y, e.aim);
    }
  }

  private onSnapshotEnd(): void {
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (this.seen[id] === 0 && this.world.alive[id] === 1 && id !== this.myId) {
        this.world.alive[id] = 0;
        this.interp.clear(id);
        this.sprites[id]?.destroy();
        this.sprites[id] = null;
        this.labels[id]?.destroy();
        this.labels[id] = null;
      }
    }
    for (let i = 0; i < MAX_PICKUPS; i++) {
      if (this.pickupSeen[i] === 0 && this.world.kAlive[i] === 1) {
        this.world.kAlive[i] = 0;
      }
    }
  }

  // ---- simulation -----------------------------------------------------------

  private tickLocal(): void {
    if (this.myId < 0 || this.matchState === MATCH_INTERMISSION) return;
    const id = this.myId;
    const k = this.keys;
    let buttons = 0;
    // Menu open: keep ticking (so the server sees keys released
    // and prediction stays in step) but read no controls at all.
    const uiOpen = this.registry.get('uiOpen') === true;
    if (uiOpen) {
      this.seq = (this.seq + 1) >>> 0;
      const aim = this.world.aim[id]!;
      applyInput(this.world, id, this.seq, 0, aim, TICK_DT);
      this.pending.push({ seq: this.seq, buttons: 0, aim, throwDist: 0 });
      if (this.pending.length > 120) this.pending.shift();
      this.net.sendInput(this.seq, 0, aim);
      return;
    }
    if (k.up.isDown) buttons |= BTN_UP;
    if (k.down.isDown) buttons |= BTN_DOWN;
    if (k.left.isDown) buttons |= BTN_LEFT;
    if (k.right.isDown) buttons |= BTN_RIGHT;
    if (k.walk.isDown) buttons |= BTN_WALK; // running is the default
    if (k.leanLeft.isDown) buttons |= BTN_LEAN_LEFT;
    if (k.leanRight.isDown) buttons |= BTN_LEAN_RIGHT;
    if (k.jump.isDown) buttons |= BTN_JUMP;
    if (k.reload.isDown) buttons |= BTN_RELOAD;
    // Rifle / grenade: the switch key toggles, 1 and 2 pick a slot, the
    // touch button toggles; all become one SWITCH press when a change is due.
    const w = this.world;
    let wantSwitch = Phaser.Input.Keyboard.JustDown(k.switchWeapon);
    if (Phaser.Input.Keyboard.JustDown(this.slotKeys.one) && w.weapon[id] !== WEAPON_RIFLE) wantSwitch = true;
    if (Phaser.Input.Keyboard.JustDown(this.slotKeys.two) && w.weapon[id] !== WEAPON_GRENADE) wantSwitch = true;
    if (this.registry.get('touchSwitch') === true) {
      this.registry.set('touchSwitch', false);
      wantSwitch = true;
    }
    if (wantSwitch) buttons |= BTN_SWITCH;
    // Held state is sent; the sim fires on the press edge (one shot per click).
    // A press that the HUD consumed (gear, score tap) is not a shot. The
    // block ends when the button is released, wherever the release landed
    // (Phaser tracks mouseup on the window, the HUD only sees the canvas).
    const pointerDown = this.input.activePointer.isDown;
    const blocked = this.registry.get('uiPointerBlock') === true; // read before clearing: a tap on the HUD is already up
    if (!pointerDown) this.registry.set('uiPointerBlock', false);
    if ((pointerDown || this.fireLatch) && !blocked) buttons |= BTN_FIRE;
    this.fireLatch = false;
    if (settings.moveMode === 'screen') buttons |= FLAG_SCREEN_RELATIVE;

    const p = this.input.activePointer;
    let aim = Phaser.Math.Angle.Between(this.world.x[id]!, this.world.y[id]!, p.worldX, p.worldY);
    // Grenade in hand: the cursor's distance is the throw distance (2 px units on the wire).
    let throwDist = Math.min(255, Math.round(Phaser.Math.Distance.Between(w.x[id]!, w.y[id]!, p.worldX, p.worldY) / 2));

    // Touch devices: twin sticks drawn by the HUD. The stick vector is
    // quantised to the 8 digital directions the protocol carries (a finger
    // on a stick is rarely more precise than that anyway) and movement is
    // always screen-relative, since there is no cursor to move toward.
    const touch = this.registry.get('touch') as TouchState | undefined;
    if (touch?.active) {
      buttons &= ~BTN_FIRE; // the raw pointer-down above is a stick touch, not a shot
      buttons |= FLAG_SCREEN_RELATIVE;
      if (touch.moveMag > 0) {
        const nx = touch.moveX / touch.moveMag;
        const ny = touch.moveY / touch.moveMag;
        if (ny < -0.38) buttons |= BTN_UP;
        if (ny > 0.38) buttons |= BTN_DOWN;
        if (nx < -0.38) buttons |= BTN_LEFT;
        if (nx > 0.38) buttons |= BTN_RIGHT;
        if (touch.moveMag < TOUCH_WALK_BELOW) buttons |= BTN_WALK; // light push walks, firm push runs
      }
      if (touch.fire) buttons |= BTN_FIRE;
      aim = touch.aiming ? touch.aim : this.world.aim[id]!;
      // Stick deflection is the throw distance (half stick = half range).
      throwDist = Math.round((GRENADE_RANGE_MIN + (GRENADE_RANGE_MAX - GRENADE_RANGE_MIN) * touch.aimMag) / 2);
    }

    this.seq = (this.seq + 1) >>> 0;
    applyInput(this.world, id, this.seq, buttons, aim, TICK_DT, throwDist);
    this.pending.push({ seq: this.seq, buttons, aim, throwDist });
    if (this.pending.length > 120) this.pending.shift(); // ~2 s with no ack
    this.net.sendInput(this.seq, buttons, aim, throwDist);

    if (this.world.shotFired[id] === 1) {
      // The bullet itself was spawned predictively inside applyInput.
      this.lastMuzzleAt = this.time.now;
      this.registry.set('ammo', this.world.ammo[id]);
      this.registry.set('reloading', this.world.reload[id]! > 0);
    }
    if (this.world.grenadeThrown[id] === 1) this.registry.set('grenades', this.world.grenades[id]);
    this.registry.set('weaponId', w.weapon[id]);
    this.registry.set('switching', w.switchTicks[id]! > 0);
    this.throwTarget = w.weapon[id] === WEAPON_GRENADE && w.switchTicks[id] === 0 ? Math.max(GRENADE_RANGE_MIN, Math.min(GRENADE_RANGE_MAX, throwDist * 2)) : -1;
  }

  // ---- rendering ------------------------------------------------------------

  // 0..1: how visible something at (x, y) is to the local player. The server
  // only sends what is within VIEW_RADIUS; across the last VIEW_FADE_PX of
  // that radius things fade in, so entering the view reads as approaching,
  // not as popping. 1 when there is no local player yet.
  private visibility(x: number, y: number): number {
    if (this.myId < 0) return 1;
    const dx = x - this.world.x[this.myId]!;
    const dy = y - this.world.y[this.myId]!;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d <= GameScene.FADE_START) return 1;
    return Math.max(0, (VIEW_RADIUS - d) / VIEW_FADE_PX);
  }

  private render(): void {
    const w = this.world;
    const now = this.time.now;
    const g = this.fx;
    g.clear();
    this.flushStaleJoins(now);

    // Remote players: resolve the interpolated pose for this frame's render
    // time into the world arrays, so bullets and sprites agree on where
    // everyone is drawn.
    const rt = this.interp.renderTick(now);
    this.interpExtrapolating = 0;
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (id === this.myId || w.alive[id] === 0) continue;
      if (this.interp.sample(id, rt, this.interpOut)) {
        w.x[id] = this.interpOut.x;
        w.y[id] = this.interpOut.y;
        w.aim[id] = this.interpOut.aim;
        if (this.interpOut.extrapolated) this.interpExtrapolating++;
      }
    }

    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (w.alive[id] === 0) continue;
      const s = this.ensureSprite(id);
      s.setVisible(w.health[id]! > 0);
      s.setPosition(w.x[id], w.y[id]);
      const teammate = this.matchMode === MODE_TEAM && w.team[id] !== NO_TEAM && w.team[id] === w.team[this.myId];
      const vis = id === this.myId || teammate ? 1 : this.visibility(w.x[id]!, w.y[id]!);
      s.setAlpha(vis);

      // Camouflaged players are drawn as their prop (no rotation, props don't
      // turn). Everyone else, including the player, sees the same thing; the
      // player also gets a faint outline so they know they're hidden.
      const prop = w.disguised[id]!;
      this.applyLook(s, id, prop);
      // Hit flash: white then red for FLASH_MS. Not on a prop: a disguise
      // that lit up when shot would be a new tell.
      const flashLeft = this.flashUntil[id]! - now;
      if (flashLeft > 0 && prop === PROP_NONE && w.health[id]! > 0) {
        s.setTintFill(flashLeft > GameScene.FLASH_MS * 0.6 ? 0xffffff : 0xff5a4a);
      }
      if (prop === PROP_NONE) {
        s.rotation = w.aim[id]!; // remote aim is already interpolated
      } else {
        s.rotation = 0;
        if (id === this.myId) g.lineStyle(1, 0xffffff, 0.35).strokeRect(s.x - 17, s.y - 17, 34, 34);
      }

      // Team match: a white outline marks you among same-coloured teammates.
      if (id === this.myId && this.matchMode === MODE_TEAM && prop === PROP_NONE && w.health[id]! > 0) {
        g.lineStyle(2, 0xffffff, 0.8).strokeCircle(s.x, s.y, PLAYER_RADIUS + 2);
      }

      // Grenade in hand (or coming out): a dark ball by the off hand, for everyone.
      if (prop === PROP_NONE && w.health[id]! > 0 && w.weapon[id] === WEAPON_GRENADE) {
        const a = w.aim[id]! + 1.1;
        g.fillStyle(0x3b4a2a, 1).fillCircle(s.x + Math.cos(a) * (PLAYER_RADIUS - 1), s.y + Math.sin(a) * (PLAYER_RADIUS - 1), 5);
      }

      // Spawn protection: a blinking ring.
      if (w.health[id]! > 0 && w.invuln[id]! > 0 && ((now / 120) | 0) % 2 === 0) {
        g.lineStyle(2, 0x9fe0ff, 0.9).strokeCircle(s.x, s.y, PLAYER_RADIUS + 5);
      }

      // Name tag for other players: soft, small, above the head; never
      // while dead or disguised (it would give the camouflage away).
      if (id !== this.myId) {
        const label = this.ensureLabel(id);
        const show = w.health[id]! > 0 && prop === PROP_NONE;
        label.setVisible(show);
        if (show) {
          const name = this.nameOf(id);
          if (label.text !== name) label.setText(name);
          label.setPosition(Math.round(s.x), Math.round(s.y - PLAYER_RADIUS - 5)).setAlpha(0.5 * vis);
        }
      }
    }

    // Pickups: sprite per live slot, gently bobbing.
    for (let i = 0; i < MAX_PICKUPS; i++) {
      let s = this.pickupSprites[i];
      if (w.kAlive[i] === 0) {
        if (s) s.setVisible(false);
        continue;
      }
      const kind = w.kKind[i];
      const key = kind === PICKUP_HEALTH ? 'kit-health' : kind === PICKUP_CAMO ? 'kit-camo' : kind === PICKUP_GRENADE ? 'kit-grenade' : 'kit-ammo';
      if (!s) {
        s = this.add.image(w.kX[i]!, w.kY[i]!, key).setDepth(-5);
        this.pickupSprites[i] = s;
      } else if (s.texture.key !== key) {
        s.setTexture(key);
      }
      s.setVisible(true).setPosition(w.kX[i], w.kY[i]! + Math.sin(now / 250 + i) * 2).setAlpha(this.visibility(w.kX[i]!, w.kY[i]!));
    }

    // Bullets: a bright head with a short trail along the direction of flight.
    // Grenades: a shadow on the ground and the body on an arc above it.
    for (let i = 0; i < MAX_PROJECTILES; i++) {
      if (w.pAlive[i] === 0) continue;
      const x = w.pX[i]!;
      const y = w.pY[i]!;
      if (w.pKind[i] === PROJ_GRENADE) {
        const t = 1 - w.pTtl[i]! / GRENADE_FLIGHT_TICKS;
        const h = w.pRange[i]! * 0.35 * 4 * t * (1 - t);
        g.fillStyle(0x000000, 0.08).fillEllipse(x, y, 10, 6);
        g.fillStyle(0x3b4a2a, 1).fillCircle(x, y - h, 5);
        g.fillStyle(0x9fb09a, 0.8).fillCircle(x - 1.5, y - h - 1.5, 1.6);
        if (((now / 80) | 0) % 2 === 0) g.fillStyle(0xffd27a, 0.9).fillCircle(x + 3, y - h - 4, 1.5);
        continue;
      }
      const vx = w.pVX[i]!;
      const vy = w.pVY[i]!;
      const len = 14 / Math.hypot(vx, vy);
      g.lineStyle(3, 0xffc866, 0.55).lineBetween(x - vx * len, y - vy * len, x, y);
      g.fillStyle(0xfff1b8, 1).fillCircle(x, y, BULLET_RADIUS);
    }

    // Explosions: a flash that fades and a ring that grows to the blast radius.
    for (let k = 0; k < GameScene.BOOM_SLOTS; k++) {
      const age = now - this.boomAt[k]!;
      if (age < 0 || age > GameScene.BOOM_MS) continue;
      const t = age / GameScene.BOOM_MS;
      g.fillStyle(0xfff1b8, 0.6 * (1 - t)).fillCircle(this.boomX[k]!, this.boomY[k]!, 14 + 30 * t);
      g.lineStyle(3, 0xff9a3c, 0.9 * (1 - t)).strokeCircle(this.boomX[k]!, this.boomY[k]!, GRENADE_RADIUS * (0.3 + 0.7 * t));
    }

    // Grenade out: where it would land, so a throw is aimed, not guessed.
    if (this.myId >= 0 && this.throwTarget > 0 && w.health[this.myId]! > 0) {
      const a = w.aim[this.myId]!;
      const tx = w.x[this.myId]! + Math.cos(a) * this.throwTarget;
      const ty = w.y[this.myId]! + Math.sin(a) * this.throwTarget;
      g.lineStyle(1, 0xffd27a, 0.5).strokeCircle(tx, ty, GRENADE_RADIUS);
      g.fillStyle(0xffd27a, 0.12).fillCircle(tx, ty, GRENADE_RADIUS);
    }

    if (this.myId >= 0 && now - this.lastMuzzleAt < MUZZLE_MS) {
      const id = this.myId;
      const mx = w.x[id]! + Math.cos(w.aim[id]!) * (PLAYER_RADIUS + 10);
      const my = w.y[id]! + Math.sin(w.aim[id]!) * (PLAYER_RADIUS + 10);
      g.fillStyle(0xfff3b0, 0.9).fillCircle(mx, my, 6);
    }

    // Hurt arc: a red arc on our rim toward where the hit came from.
    if (this.myId >= 0 && now - this.hurtAt < GameScene.HURT_MS && !Number.isNaN(this.hurtAngle) && w.health[this.myId]! > 0) {
      const t = 1 - (now - this.hurtAt) / GameScene.HURT_MS;
      g.lineStyle(4, 0xff3b2f, 0.9 * t);
      g.beginPath();
      g.arc(w.x[this.myId]!, w.y[this.myId]!, PLAYER_RADIUS + 6, this.hurtAngle - 0.6, this.hurtAngle + 0.6, false);
      g.strokePath();
    }

    if (now - this.lastKillMarkerAt < 400) {
      // Kill: a bigger red cross that fades.
      const p = this.input.activePointer;
      const cx = p.worldX;
      const cy = p.worldY;
      const t = 1 - (now - this.lastKillMarkerAt) / 400;
      g.lineStyle(3, 0xff5a4a, 0.95 * t);
      g.lineBetween(cx - 16, cy - 16, cx - 5, cy - 5);
      g.lineBetween(cx + 16, cy - 16, cx + 5, cy - 5);
      g.lineBetween(cx - 16, cy + 16, cx - 5, cy + 5);
      g.lineBetween(cx + 16, cy + 16, cx + 5, cy + 5);
    } else if (now - this.lastHitMarkerAt < HIT_MARKER_MS) {
      const p = this.input.activePointer;
      const cx = p.worldX;
      const cy = p.worldY;
      g.lineStyle(2, 0xffffff, 0.95);
      g.lineBetween(cx - 10, cy - 10, cx - 4, cy - 4);
      g.lineBetween(cx + 10, cy - 10, cx + 4, cy - 4);
      g.lineBetween(cx - 10, cy + 10, cx - 4, cy + 4);
      g.lineBetween(cx + 10, cy + 10, cx + 4, cy + 4);
    }

    this.registry.set(
      'debug',
      `${this.status}  id:${this.myId}  ${this.level.name} ${this.level.cols}x${this.level.rows}  seq:${this.seq}  pending:${this.pending.length}  ` +
        `interp:${Math.round((INTERP_DELAY_TICKS * 1000) / TICK_RATE)}ms buf:${Math.round(this.interp.headroomMs(this.time.now))}ms` +
        (this.interpExtrapolating > 0 ? ` extrap:${this.interpExtrapolating}` : '') +
        (import.meta.env.DEV ? ` corr:${((this.registry.get('reconcile') as { corrections: number } | undefined) ?? { corrections: 0 }).corrections}` : '') +
        '  ' +
        (settings.moveMode === 'screen' ? 'WASD screen' : 'W/S to cursor, A/D strafe') +
        `  ${settings.bindings.walk.label} walk  click fire  ${settings.bindings.reload.label} reload  Tab scores`,
    );
  }

  private ensureLabel(id: number): Phaser.GameObjects.Text {
    let t = this.labels[id];
    if (!t) {
      t = this.add
        .text(0, 0, '', { fontFamily: 'monospace', fontSize: '11px', color: '#e6efe0', resolution: TEXT_RESOLUTION })
        .setOrigin(0.5, 1)
        .setAlpha(0.5)
        .setDepth(4); // above soldiers (0), below the fx layer (5)
      this.labels[id] = t;
    }
    return t;
  }

  private ensureSprite(id: number): Phaser.GameObjects.Image {
    let s = this.sprites[id];
    if (!s) {
      s = this.add.image(this.world.x[id]!, this.world.y[id]!, this.soldierKey(id), 'idle');
      this.sprites[id] = s;
    }
    return s;
  }

  // Swap a player's sprite between the soldier and a prop. Props sit at
  // ground depth like real pickups/tiles so they don't float above things.
  private applyLook(s: Phaser.GameObjects.Image, id: number, prop: number): void {
    // Team match: everyone wears their team colour (tinted soldier), you
    // get a white outline ring from render(). Deathmatch: you green, the
    // rest red, as before.
    const want = prop === PROP_NONE ? this.soldierKey(id) : PROP_TEXTURE[prop]!;
    const frame = prop === PROP_BRICK ? 'wall' : prop === PROP_CRATE ? 'crate' : prop === PROP_NONE ? this.walkFrame(id) : undefined;
    if (s.texture.key !== want || (frame !== undefined && s.frame.name !== frame)) {
      s.setTexture(want, frame);
    }
    s.clearTint(); // the hit flash re-applies its tint after this each frame
    s.setDepth(prop === PROP_NONE ? 0 : -5);
  }

  // Team colour by sheet (one sheet per uniform colour, no tinting, so skin,
  // rifle and boots keep their own colours).
  private soldierKey(id: number): string {
    const team = this.world.team[id]!;
    if (this.matchMode === MODE_TEAM && team !== NO_TEAM) return SOLDIER_KEYS.team(team);
    return id === this.myId ? SOLDIER_KEYS.me : SOLDIER_KEYS.enemy;
  }

  // Walk cycle driven by how far the sprite moved since the last frame
  // (works the same for our predicted soldier and interpolated others):
  // one cycle per ~100 px, idle when standing.
  private readonly walkPrevX = new Float32Array(MAX_PLAYERS);
  private readonly walkPrevY = new Float32Array(MAX_PLAYERS);
  private readonly walkPhase = new Float32Array(MAX_PLAYERS);
  private walkFrame(id: number): string {
    const x = this.world.x[id]!;
    const y = this.world.y[id]!;
    const moved = Math.hypot(x - this.walkPrevX[id]!, y - this.walkPrevY[id]!);
    this.walkPrevX[id] = x;
    this.walkPrevY[id] = y;
    if (moved < 0.3 || moved > 200) return 'idle'; // still, or a teleport
    const phase = (this.walkPhase[id]! + moved / 100) % 1;
    this.walkPhase[id] = phase;
    return `walk${Math.floor(phase * SOLDIER_WALK_FRAMES)}`;
  }

  // Placeholder art generated in code. Original, no third-party assets.
  private makeTextures(): void {
    if (this.textures.exists('soldier')) return;
    makeSoldierSheets(this); // original soldier sprites (soldier-art.ts)

    // Health kit: white box, red cross. Ammo crate: olive box, brass band.
    const kit = this.make.graphics({ x: 0, y: 0 }, false);
    kit.fillStyle(0x000000, 0.35).fillRoundedRect(2, 3, 20, 20, 3); // shadow
    kit.fillStyle(0xf2f2ea, 1).fillRoundedRect(0, 0, 20, 20, 3);
    kit.fillStyle(0xd23c3c, 1).fillRect(8, 3, 4, 14).fillRect(3, 8, 14, 4);
    kit.generateTexture('kit-health', 24, 24);
    kit.destroy();

    const crate = this.make.graphics({ x: 0, y: 0 }, false);
    crate.fillStyle(0x000000, 0.35).fillRect(2, 3, 22, 14); // shadow
    crate.fillStyle(0x5d6b3a, 1).fillRect(0, 0, 22, 14);
    crate.fillStyle(0xc9a648, 1).fillRect(0, 5, 22, 4);
    crate.lineStyle(1, 0x2f371f, 1).strokeRect(0.5, 0.5, 21, 13);
    crate.generateTexture('kit-ammo', 24, 18);
    crate.destroy();

    // Grenade pouch: a dark olive bag with two grenades peeking out.
    const pouch = this.make.graphics({ x: 0, y: 0 }, false);
    pouch.fillStyle(0x000000, 0.35).fillRoundedRect(2, 3, 22, 18, 4); // shadow
    pouch.fillStyle(0x4a5530, 1).fillRoundedRect(0, 0, 22, 18, 4);
    pouch.fillStyle(0x2d3520, 1).fillRect(0, 6, 22, 3); // strap
    pouch.fillStyle(0x3b4a2a, 1).fillCircle(7, 12, 4).fillCircle(15, 12, 4);
    pouch.fillStyle(0xffd27a, 1).fillRect(5, 11, 4, 1.5).fillRect(13, 11, 4, 1.5); // the stripe grenades wear
    pouch.generateTexture('kit-grenade', 24, 22);
    pouch.destroy();

    // Camouflage pickup: a folded net, four-colour blotches with a dark edge.
    const camo = this.make.graphics({ x: 0, y: 0 }, false);
    camo.fillStyle(0x000000, 0.35).fillRoundedRect(2, 3, 22, 22, 4);
    camo.fillStyle(0x4f5f3a, 1).fillRoundedRect(0, 0, 22, 22, 4);
    camo.fillStyle(0x8a7a4a, 1).fillCircle(6, 7, 5).fillCircle(16, 15, 5);
    camo.fillStyle(0x2f3b26, 1).fillCircle(15, 5, 4).fillCircle(6, 16, 4);
    camo.fillStyle(0xa9a07a, 1).fillCircle(11, 11, 3);
    camo.lineStyle(1, 0x1e2418, 1).strokeRoundedRect(0.5, 0.5, 21, 21, 4);
    camo.generateTexture('kit-camo', 26, 26);
    camo.destroy();

    // Prop frames cut from the tileset strip (indices in tools/tileset.ts).
    const tiles = this.textures.get(CAMO_SHEET_KEY);
    if (!tiles.has('wall')) tiles.add('wall', 0, 4 * 32, 0, 32, 32);
    if (!tiles.has('crate')) tiles.add('crate', 0, 7 * 32, 0, 32, 32);
  }
}
